import * as XLSX from 'xlsx';

/** Registry import accepts only these headers (hcpLicensesListController.readHcpRegistryImportRow). */
export const HCP_REGISTRY_IMPORT_HEADERS = [
  'match_hcp_emirates_id',
  'hcp_name',
  'hcp_email',
  'hcp_phone',
  'license_number',
  'license_issue_date',
  'license_end_date',
  'total_cme_hours',
  'end_user_email',
];

const DEMO_ROW = [
  '123456789012345',
  'Sample HCP',
  'hcp.sample@example.com',
  '+971501234567',
  'LIC-0001',
  '2024-01-15',
  '2026-01-15',
  '',
  'applicant@example.com',
];

function formatYmdLocal(d) {
  if (!d || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Match backend `parseSheetDate`: DD/MM/YYYY slash dates (JS `Date("28/05/2026")` is invalid). */
function parseSlashDateToYmd(s) {
  const m = String(s)
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return '';
  const a = parseInt(m[1], 10);
  const b = parseInt(m[2], 10);
  const y = parseInt(m[3], 10);
  let day;
  let month;
  if (a > 12) {
    day = a;
    month = b;
  } else if (b > 12) {
    month = a;
    day = b;
  } else {
    day = a;
    month = b;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  const d = new Date(y, month - 1, day);
  if (
    d.getFullYear() !== y ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return '';
  }
  return formatYmdLocal(d);
}

function formatDateOnlyForCell(val) {
  if (val == null || val === '') return '';
  const s = String(val).trim();
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const slash = parseSlashDateToYmd(s);
  if (slash) return slash;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return formatYmdLocal(d);
  }
  return s;
}

/**
 * @param {string} [fileBaseName] — without extension
 */
export function downloadHcpRegistryImportTemplate(
  fileBaseName = 'hcp-licenses-registry-import-template'
) {
  const aoa = [HCP_REGISTRY_IMPORT_HEADERS, DEMO_ROW];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'HCP Registry');
  const safe = String(fileBaseName || 'template').replace(
    /[/\\?%*:|"<>]/g,
    '-'
  );
  XLSX.writeFile(wb, `${safe}.xlsx`);
}

/**
 * Export current registry rows in the same column layout as the import API expects (re-import / bulk update).
 * @param {Array<object>} rows — API `/api/hcp-licenses-list` row objects
 * @param {string} [fileBaseName]
 */
export function exportHcpRegistryRowsForReimport(
  rows,
  fileBaseName = 'hcp-licenses-registry-export'
) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return false;
  }
  const aoa = [HCP_REGISTRY_IMPORT_HEADERS];
  for (const row of rows) {
    aoa.push([
      row.hcp_emirates_id != null
        ? String(row.hcp_emirates_id).replace(/\D/g, '')
        : '',
      row.hcp_name != null ? String(row.hcp_name).trim() : '',
      row.hcp_email != null ? String(row.hcp_email).trim() : '',
      row.hcp_phone != null ? String(row.hcp_phone).trim() : '',
      row.license_number != null ? String(row.license_number).trim() : '',
      formatDateOnlyForCell(row.license_issue_date),
      formatDateOnlyForCell(row.license_end_date),
      row.total_cme_hours != null && row.total_cme_hours !== ''
        ? String(row.total_cme_hours)
        : '',
      row.end_user?.email != null ? String(row.end_user.email).trim() : '',
    ]);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'HCP Registry');
  const safe = String(fileBaseName || 'export')
    .replace(/[/\\?%*:|"<>]/g, '-')
    .slice(0, 120);
  XLSX.writeFile(wb, `${safe}.xlsx`);
  return true;
}
